import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type HeaderProps = {
  language: Language;
  onLanguageChange: (lang: Language) => void;
};
const navItems = {
  ua: ['Про нас', 'Продукція', 'Технічний центр', 'Цифровий центр', 'Контакти'],
  en: ['About', 'Products', 'Technical Center', 'Digital Center', 'Contact']
};
const ctaText = {
  ua: 'Стати партнером',
  en: 'Become a Partner'
};
export function Header({
  language,
  onLanguageChange
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);
  return <>
      <motion.header initial={{
      y: -100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }} className={`fixed top-4 left-4 right-4 lg:left-0 lg:right-0 lg:mx-auto lg:w-[calc(100%-2rem)] lg:max-w-7xl z-50 transition-all duration-500 rounded-2xl lg:rounded-full border ${isScrolled ? 'bg-black/30 backdrop-blur-xl border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'bg-black/20 backdrop-blur-md border-white/10 shadow-lg'}`} data-id="element-110">
        <div className="px-4 lg:px-8" data-id="element-111">
          <div className="flex items-center justify-between h-14 lg:h-16" data-id="element-112">
            {/* Logo */}
            <motion.a href="#" className="flex items-center gap-2 group relative z-50" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-113">
              <div className="relative flex items-center h-6 sm:h-8" data-id="element-114">
                <img src="https://cdn.magicpatterns.com/uploads/dPC9v62jFFRaqiBcJdHaJe/chamaeleon_logo_white-yellow_RGB_копія.png" alt="CHAMÄLEON" className="h-full w-auto object-contain" data-id="element-115" />
              </div>
            </motion.a>

            {/* Desktop Navigation (Centered) */}
            <nav className="hidden lg:flex items-center justify-center absolute left-1/2 -translate-x-1/2 gap-8" data-id="element-116">
              {navItems[language].map((item, index) => <motion.a key={item} href="#" className="text-sm font-medium text-white/80 hover:text-white transition-colors relative group py-2" initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05 + 0.3
            }} data-id="element-117">
                  {item}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-chamaleon-yellow group-hover:w-full transition-all duration-300 rounded-full" data-id="element-118" />
                </motion.a>)}
            </nav>

            {/* Desktop Right Section (Lang + CTA) */}
            <div className="hidden lg:flex items-center gap-6" data-id="element-119">
              {/* Language Switcher */}
              <div className="relative" data-id="element-120">
                <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white transition-colors" data-id="element-121">
                  {language.toUpperCase()}
                  <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${isLangMenuOpen ? 'rotate-180' : ''}`} data-id="element-122" />
                </button>

                <AnimatePresence data-id="element-123">
                  {isLangMenuOpen && <motion.div initial={{
                  opacity: 0,
                  y: 10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 10,
                  scale: 0.95
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full right-0 mt-4 w-24 bg-chamaleon-black/90 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50" data-id="element-124">
                      {(['en', 'ua'] as Language[]).map(lang => <button key={lang} onClick={() => {
                    onLanguageChange(lang);
                    setIsLangMenuOpen(false);
                  }} className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${language === lang ? 'bg-chamaleon-yellow/10 text-chamaleon-yellow font-medium' : 'text-white/70 hover:bg-white/5 hover:text-white'}`} data-id="element-125">
                          {lang.toUpperCase()}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* CTA Button */}
              <motion.a href="#partner" className="inline-flex items-center justify-center px-5 py-2 bg-chamaleon-yellow text-chamaleon-black text-sm font-bold rounded-full hover:bg-[#E5A619] transition-all shadow-[0_0_15px_rgba(255,184,28,0.2)] hover:shadow-[0_0_25px_rgba(255,184,28,0.4)]" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5
            }} data-id="element-126">
                {ctaText[language]}
              </motion.a>
            </div>

            {/* Mobile Right Section */}
            <div className="flex lg:hidden items-center gap-4 relative z-50" data-id="element-127">
              {/* Mobile Language Switcher */}
              <div className="relative" data-id="element-128">
                <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-1 text-sm font-medium text-white" data-id="element-129">
                  {language.toUpperCase()}
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} data-id="element-130" />
                </button>

                <AnimatePresence data-id="element-131">
                  {isLangMenuOpen && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute top-full right-0 mt-4 w-24 bg-chamaleon-black/95 border border-white/10 rounded-xl shadow-xl overflow-hidden z-50" data-id="element-132">
                      {(['en', 'ua'] as Language[]).map(lang => <button key={lang} onClick={() => {
                    onLanguageChange(lang);
                    setIsLangMenuOpen(false);
                  }} className={`w-full text-left px-4 py-2 text-sm transition-colors ${language === lang ? 'bg-chamaleon-yellow/10 text-chamaleon-yellow font-medium' : 'text-gray-300 hover:bg-white/5 hover:text-white'}`} data-id="element-133">
                          {lang.toUpperCase()}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Mobile Menu Button */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-1 text-white" aria-label="Toggle menu" data-id="element-134">
                {isMobileMenuOpen ? <XIcon size={24} data-id="element-135" /> : <MenuIcon size={24} data-id="element-136" />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Full Screen Mobile Menu Overlay */}
      <AnimatePresence data-id="element-137">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        backdropFilter: 'blur(0px)'
      }} animate={{
        opacity: 1,
        backdropFilter: 'blur(20px)'
      }} exit={{
        opacity: 0,
        backdropFilter: 'blur(0px)'
      }} transition={{
        duration: 0.4
      }} className="fixed inset-0 z-40 bg-chamaleon-black/90 flex flex-col justify-center px-6" data-id="element-138">
            <nav className="flex flex-col items-center space-y-6" data-id="element-139">
              {navItems[language].map((item, index) => <motion.a key={item} href="#" className="text-2xl sm:text-3xl font-bold text-white/80 hover:text-white transition-colors" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} onClick={() => setIsMobileMenuOpen(false)} data-id="element-140">
                  {item}
                </motion.a>)}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="pt-8 w-full max-w-xs" data-id="element-141">
                <a href="#partner" className="flex items-center justify-center w-full px-6 py-4 bg-chamaleon-yellow text-chamaleon-black font-bold rounded-full hover:bg-[#E5A619] transition-colors text-lg" onClick={() => setIsMobileMenuOpen(false)} data-id="element-142">
                  {ctaText[language]}
                </a>
              </motion.div>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </>;
}